import { IconChatMoney } from "@/components/design-system/icons/IconChatMoney";
import { IconDataServer } from "@/components/design-system/icons/IconDataServer";
import { IconHiFive } from "@/components/design-system/icons/IconHiFive";
import { IconLaunch } from "@/components/design-system/icons/IconLaunch";
import { IconMoneyGrowth } from "@/components/design-system/icons/IconMoneyGrowth";
import { IconSearchInvoice } from "@/components/design-system/icons/IconSearchInvoice";
import { IconSearchWeb } from "@/components/design-system/icons/IconSearchWeb";
import { IconWireframe } from "@/components/design-system/icons/IconWireframe";
import { IconArrowRight } from "@/components/design-system/basic-icons/IconArrowRight";

import React, { useState } from "react";
import { Link } from "react-router-dom";

type TabId = "wealth" | "credit" | "employers";

type FeatureItem = {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
};

type TabData = {
  readonly id: TabId;
  readonly label: string;
  readonly heading: React.ReactNode;
  readonly description: string;
  readonly href: string;
  readonly imageSrc: string;
  readonly features: readonly FeatureItem[];
};

const iconClass = "text-primary-500 h-20 w-20 md:h-24 md:w-24";

const tabs: readonly TabData[] = [
  {
    id: "wealth",
    label: "Wealth Managers",
    heading: (
      <>
        Surface <span className="text-primary-500">hidden assets</span>. <br />
        Scale advisor relationships.
      </>
    ),
    description:
      "Bravo turns plan sponsor relationships into an asset discovery engine identifying orphan 401(k)s, held-away accounts, and clients ready to consolidate before advisors ever pick up the phone.",
    href: "/wealthmanagers",
    imageSrc: "/wm-chat.png",
    features: [
      {
        icon: <IconSearchWeb className={iconClass} />,
        title: "Intelligent asset flagging",
        description:
          "Surfaces orphan 401(k)s, idle cash, and held-away brokerage accounts automatically.",
      },
      {
        icon: <IconHiFive className={iconClass} />,
        title: "Warm advisor handoffs",
        description:
          "Context-rich referrals so advisors enter conversations already informed.",
      },
      {
        icon: <IconLaunch className={iconClass} />,
        title: "Compounding AUM growth",
        description:
          "Across thousands of participants, even modest conversion is transformational.",
      },
    ],
  },
  {
    id: "credit",
    label: "Credit Unions",
    heading: (
      <>
        Your members are turning to AI for money guidance.
        <span className="text-primary-500"> Make sure they turn to you.</span>
      </>
    ),
    description:
      "Bravo gives members a trusted, secure AI guide for questions about spending, saving, debt, credit, and planning — keeping financial conversations inside your credit union and boosting cross-sell opportunities.",
    href: "/creditunions",
    imageSrc: "/cu-chat.png",
    features: [
      {
        icon: <IconChatMoney className={iconClass} />,
        title: "Conversational member guidance",
        description:
          "Personalized coaching based on each member's real financial picture.",
      },
      {
        icon: <IconSearchInvoice className={iconClass} />,
        title: "Real-time account aggregation",
        description:
          "Always-on account visibility surfaces savings gaps, lending needs, and cross-sell opportunities automatically.",
      },
      {
        icon: <IconWireframe className={iconClass} />,
        title: "Fully white-labeled",
        description:
          "Your brand, your name, your voice — Bravo stays invisible so you stay top of mind.",
      },
    ],
  },
  {
    id: "employers",
    label: "Employers",
    heading: (
      <>
        A financial benefit{" "}
        <span className="text-primary-500">employees actually use.</span>
      </>
    ),
    description:
      "Replace static benefit portals with a conversational financial health companion — paycheck questions, benefit elections, debt, retirement — one intelligent interface for every financial moment at work.",
    href: "/employers",
    imageSrc: "/emp-chat.png",
    features: [
      {
        icon: <IconMoneyGrowth className={iconClass} />,
        title: "Benefit navigation made simple",
        description:
          "Instant, personalized answers about HSAs, FSAs, 401(k) matching, and more.",
      },
      {
        icon: <IconHiFive className={iconClass} />,
        title: "Whole-picture financial coaching",
        description:
          "Debt, savings, budgeting — Bravo guides employees through every decision.",
      },
      {
        icon: <IconDataServer className={iconClass} />,
        title: "Built-in compliance",
        description:
          "Stay ahead of the innovation curve while delivering personalized financial insights with the empathy and guardrails financial wellness demands.",
      },
    ],
  },
];

const SolutionFeature: React.FC<FeatureItem> = ({
  icon,
  title,
  description,
}) => (
  <div className="flex flex-col items-start gap-4">
    {icon}
    <h4 className="subtitle-b-extrabold md:subtitle-b-bold text-primary-500">
      {title}
    </h4>
    <p className="body-b-regular text-black">{description}</p>
  </div>
);

const TabContent: React.FC<TabData> = ({
  heading,
  description,
  href,
  imageSrc,
  features,
}) => (
  <div>
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="flex flex-col gap-6 md:min-w-0 md:flex-1 md:gap-4 xl:w-138 xl:flex-none">
        <h3 className="headline-d-extrabold md:headline-c-extrabold text-black">
          {heading}
        </h3>
        <p className="body-a-regular md:subtitle-b-regular text-black">
          {description}
        </p>
        <Link
          to={href}
          className="body-b-semibold bg-primary-500 inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-2 text-white md:w-auto md:self-start"
        >
          Learn more
          <IconArrowRight className="h-5 w-5" />
        </Link>
      </div>
      <div className="flex justify-center md:min-w-0 md:flex-1 md:justify-end xl:flex-none">
        <img
          src={imageSrc}
          alt=""
          className="rounded-5 h-auto w-full max-w-114.25"
        />
      </div>
    </div>
    <div className="mt-10 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3 md:gap-6">
      {features.map((feature) => (
        <SolutionFeature key={feature.title} {...feature} />
      ))}
    </div>
  </div>
);

export const TabbedSolutionsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>("wealth");
  const activeTabData = tabs.find((t) => t.id === activeTab)!;

  return (
    <section className="w-full">
      <div className="section-inset pt-12 pb-12 md:py-20">
        <div>
          <div className="mb-8 flex border-b border-gray-200 md:mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`caption-regular md:body-a-regular flex-1 cursor-pointer py-3 text-center ${activeTab === tab.id ? "tab-active" : "tab-inactive"}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <TabContent key={activeTab} {...activeTabData} />
        </div>
      </div>
    </section>
  );
};
